<template>
  <div class="file-upload-wrapper">
    <h5 v-if="label" class="upload-label">{{ label }}</h5>
    <div class="file-upload-container" :class="{ 'has-image': images.length > 0 }" @click="triggerFileInput">
      <input 
        type="file" 
        ref="fileInput" 
        accept="image/*" 
        capture="environment" 
        @change="handleFiles"
        style="display: none" 
      />

      <!-- Empty state with camera icon -->
      <div v-if="images.length === 0 && !loading" class="upload-empty-state">
        <div class="camera-section">
          <i class="pi pi-camera upload-icon"></i>
        </div>
        <div class="text-section">
          <span class="upload-text">Photo</span>
          <span class="upload-subtext">Cliquer</span>
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
        <div class="loading-text">{{ uploadProgress }}%</div>
      </div>

      <!-- Images preview -->
      <div v-if="images.length > 0 && !loading" class="image-preview">
        <div v-for="(image, index) in images" :key="image.id" class="preview-item">
          <img :src="getImageUrl(image.path)" class="preview-image" @error="handleImageError(index)" />
          <button @click.stop="removeImage(index)" class="remove-button">
            <i class="pi pi-times"></i>
          </button>
        </div>
        <div v-if="images.length < maxImages" class="preview-item add-more">
          <div class="camera-section small">
            <i class="pi pi-camera upload-icon"></i>
          </div>
        </div>
      </div>
    </div>
    <div v-if="error" class="error-message">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineExpose } from 'vue'
import axios from '@/api/axios'

const props = defineProps({
  label: String,
  resetTrigger: Boolean,
  modelValue: {
    type: Array,
    default: () => []
  },
  maxImages: {
    type: Number,
    default: 4
  }
})

const emit = defineEmits(['update:imageIds', 'error', 'success', 'reset'])

const images = ref([])
const loading = ref(false)
const error = ref(null)
const uploadProgress = ref(0)
const fileInput = ref(null)

const getImageUrl = (path) => {
  if (!path) return ''
  if (path.startsWith('http')) {
    return path
  }
  return `${import.meta.env.VITE_APP_BASE_URL}${path}`
}

const triggerFileInput = () => {
  if (images.value.length >= props.maxImages) {
    error.value = `Maximum ${props.maxImages} images allowed`
    return
  }
  fileInput.value?.click()
}

const compressImage = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)

    reader.onload = (event) => {
      const img = new Image()
      img.src = event.target.result

      img.onload = () => {
        const canvas = document.createElement('canvas')
        const MAX_WIDTH = 1200
        const MAX_HEIGHT = 1200
        let width = img.width
        let height = img.height

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width
            width = MAX_WIDTH
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height
            height = MAX_HEIGHT
          }
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          }))
        }, 'image/jpeg', 0.7)
      }

      img.onerror = reject
    }

    reader.onerror = reject
  })
}

const uploadImage = async (file) => {
  loading.value = true
  uploadProgress.value = 0
  error.value = null

  const formData = new FormData()
  formData.append('image', file)

  try {
    console.log('Starting upload...')
    const response = await axios.post('admin-explo/images/', formData, {
      onUploadProgress: (progressEvent) => {
        uploadProgress.value = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        )
        console.log('Upload progress:', uploadProgress.value)
      }
    })
    console.log('Upload successful:', response.data)

    const imageData = {
      id: response.data.id,
      path: response.data.image,
      uploadedAt: response.data.uploaded_at
    }

    images.value.push(imageData)
    updateParent()
    emit('success', imageData.id)

  } catch (err) {
    console.error('Upload failed:', err.response || err)
    throw err
  } finally {
    loading.value = false
    uploadProgress.value = 0
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }
}

const handleFiles = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return

  console.log('File received:', {
    type: file.type,
    size: file.size,
    name: file.name
  })

  if (!file.type.startsWith('image/')) {
    error.value = 'Only image files are allowed'
    return
  }

  try {
    const compressedFile = await compressImage(file)
    console.log('Compressed file size:', compressedFile.size)
    await uploadImage(compressedFile)
  } catch (err) {
    console.error('Error:', err)
    error.value = 'Failed to process image'
  }
}

const handleImageError = (index) => {
  error.value = `Failed to load image preview ${index + 1}`
}

const removeImage = (index) => {
  images.value.splice(index, 1)
  updateParent()
}

const updateParent = () => {
  const imageIds = images.value.map(image => image.id)
  emit('update:imageIds', imageIds)
}

const reset = () => {
  images.value = []
  updateParent()
  emit('reset')
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

// Watch for reset trigger changes
watch(() => props.resetTrigger, (newVal, oldVal) => {
  if (newVal !== oldVal && newVal === true) {
    reset()
  }
})

// Expose the reset method so it can be called by parent components
defineExpose({
  reset
})
</script>

<style scoped>
.file-upload-wrapper {
  width: 100%;
  margin: 0 auto;
}

.upload-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.25rem;
}

.file-upload-container {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 6px;
  width: 100%;
  height: 60px;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
}

.file-upload-container.has-image {
  height: 80px;
}

.upload-empty-state {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0.25rem 0.5rem;
}

.camera-section {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
  min-width: 35px;
  background-color: #e9ecef;
  border-radius: 6px;
  margin-right: 0.5rem;
}

.camera-section.small {
  width: 30px;
  height: 30px;
  min-width: 30px;
}

.upload-icon {
  font-size: 1rem;
  color: #6c757d;
}

.text-section {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.upload-text {
  font-size: 0.75rem;
  font-weight: 500;
  color: #495057;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.upload-subtext {
  font-size: 0.625rem;
  color: #6c757d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.image-preview {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
  gap: 0.25rem;
  padding: 0.25rem;
}

.preview-item {
  position: relative;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
}

.preview-item.add-more {
  background-color: #f8f9fa;
  border: 1px dashed #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-item.add-more .camera-section {
  margin-right: 0;
  background-color: transparent;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.remove-button {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 0.75rem;
}

.remove-button:hover {
  background-color: rgba(231, 76, 60, 0.8);
}

.loading-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 100%;
  padding: 0 0.5rem;
}

.loading-spinner {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  flex-shrink: 0;
}

.loading-text {
  font-size: 0.75rem;
  color: #3498db;
}

.error-message {
  color: #dc3545;
  font-size: 0.625rem;
  margin-top: 0.25rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (min-width: 640px) {
  .file-upload-container {
    height: 70px;
  }

  .file-upload-container.has-image {
    height: 90px;
  }

  .camera-section {
    width: 40px;
    height: 40px;
    min-width: 40px;
  }

  .upload-icon {
    font-size: 1.25rem;
  }

  .upload-text {
    font-size: 0.875rem;
  }

  .upload-subtext {
    font-size: 0.75rem;
  }
}

@media (min-width: 1024px) {
  .file-upload-container {
    height: 80px;
  }

  .file-upload-container.has-image {
    height: 100px;
  }

  .camera-section {
    width: 45px;
    height: 45px;
    min-width: 45px;
  }

  .upload-icon {
    font-size: 1.5rem;
  }
}
</style>